import {
  getInspectionReservationQuery, getInspectionQuery, getChecklistQuery, getDisputeQuery, getInspectionComparisonQuery,
  saveInspectionDraftMutation, validateInspectionMutation, contestInspectionMutation, submitCheckoutMutation,
  openDisputeMutation, addDisputeStatementMutation, writeReviewMutation,
  getInspectionSettingsQuery, setInspectionSettingsMutation, decideDisputeMutation, updateDisputeStatusMutation,
  getAdminInspectionReservationQuery
} from '../../lib/graphql/inspection';

// Thunks of the check-in / check-out feature (WEB status convention: 'success' | 'notLoggedIn' | 'failed').
// Every action resolves to the result object of the server or to { status: 'failed', errorMessage: null } when the request itself failed
// (network, unexpected GraphQL error): the components then show a translated generic message.

const failure = { status: 'failed', errorMessage: null };

const runQuery = (query, name, variables) => async (dispatch, getState, { client }) => {
  try {
    const { data } = await client.query({ query, variables, fetchPolicy: 'network-only' });
    if (data && data[name]) return data[name];
    // A null answer is "no such record for this user" (for example a reservation of somebody else), not a failed request.
    return data && name in data ? { status: 'notFound', errorMessage: null } : failure;
  } catch (error) {
    return failure;
  }
};

const runMutation = (mutation, name, variables) => async (dispatch, getState, { client }) => {
  try {
    const { data } = await client.mutate({ mutation, variables });
    return data && data[name] ? data[name] : failure;
  } catch (error) {
    return failure;
  }
};

export const fetchInspectionReservation = (reservationId) => runQuery(getInspectionReservationQuery, 'getItinerary', { reservationId: Number(reservationId) });
export const fetchInspection = (reservationId, type) => runQuery(getInspectionQuery, 'getInspection', { reservationId: Number(reservationId), type });
export const fetchChecklist = (reservationId) => runQuery(getChecklistQuery, 'getChecklist', { reservationId: Number(reservationId) });
export const fetchDispute = (reservationId) => runQuery(getDisputeQuery, 'getDispute', { reservationId: Number(reservationId) });
export const fetchInspectionComparison = (reservationId) => runQuery(getInspectionComparisonQuery, 'getInspectionComparison', { reservationId: Number(reservationId) });

export const saveInspectionDraft = ({ reservationId, type, input, expectedVersion }) => runMutation(
  saveInspectionDraftMutation, 'saveInspectionDraft',
  { reservationId: Number(reservationId), type, input, expectedVersion: expectedVersion === undefined || expectedVersion === null ? null : Number(expectedVersion) }
);

export const validateInspection = ({ reservationId, type, version }) => runMutation(
  validateInspectionMutation, 'validateInspection', { reservationId: Number(reservationId), type, version: Number(version) }
);

export const contestInspection = ({ reservationId, type, comment }) => runMutation(
  contestInspectionMutation, 'contestInspection', { reservationId: Number(reservationId), type, comment }
);

export const submitCheckout = ({ reservationId, hasDamage, input }) => runMutation(
  submitCheckoutMutation, 'submitCheckout', { reservationId: Number(reservationId), hasDamage: Boolean(hasDamage), input }
);

export const openDispute = ({ reservationId, reasonCode, comment }) => runMutation(
  openDisputeMutation, 'openDispute', { reservationId: Number(reservationId), reasonCode, comment }
);

export const addDisputeStatement = ({ disputeId, content, attachments }) => runMutation(
  addDisputeStatementMutation, 'addDisputeStatement', { disputeId: Number(disputeId), content, attachments: attachments || [] }
);

// Review of the other party through the existing module (R11). A separate call: its failure never blocks the check-out.
export const writeInspectionReview = ({ reservationId, listId, receiverId, reviewContent, rating }) => runMutation(
  writeReviewMutation, 'writeReview',
  { reservationId: Number(reservationId), listId: Number(listId), receiverId: String(receiverId), reviewContent, rating: Number(rating) }
);

// ---------- admin ----------

export const fetchInspectionSettings = () => runQuery(getInspectionSettingsQuery, 'getInspectionSettings', {});
export const saveInspectionSettings = (settings) => runMutation(setInspectionSettingsMutation, 'setInspectionSettings', settings);
export const decideDispute = (args) => runMutation(decideDisputeMutation, 'decideDispute', args);
export const changeDisputeStatus = ({ disputeId, status }) => runMutation(updateDisputeStatusMutation, 'updateDisputeStatus', { disputeId: Number(disputeId), status });
export const fetchAdminInspectionReservation = (id) => runQuery(getAdminInspectionReservationQuery, 'viewReservationAdmin', { id: Number(id) });
